import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Typography, Row, Col, Statistic, Divider } from 'antd';
import { DollarOutlined, FileProtectOutlined, WarningOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { clientService } from '../../../common/services';
import Loader from '../../../components/loader/Loader';
import moment from 'moment';

const { Title } = Typography;

export default function Situation() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadSituation();
    }, []);

    const loadSituation = async () => {
        try {
            const resp = await clientService.getSituation();
            if (resp.success) {
                setData(resp.data);
            }
        } catch (error) {
            toast.error('Erreur lors du chargement de la situation');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <Loader />;
    if (!data) return null;

    const columns = [
        {
            title: 'N. Police',
            dataIndex: ['contrat', 'numeroPolice'],
            key: 'police',
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: 'Compagnie',
            dataIndex: ['contrat', 'compagnie', 'nom'],
            key: 'compagnie',
        },
        {
            title: 'Type',
            dataIndex: ['contrat', 'typeAssurance', 'nom'],
            key: 'type',
        },
        {
            title: 'Echeance',
            dataIndex: ['contrat', 'dateEcheance'],
            key: 'echeance',
            render: (d) => moment(d).format('DD/MM/YYYY'),
        },
        {
            title: 'Statut',
            dataIndex: ['contrat', 'statut'],
            key: 'statut',
            render: (s) => {
                const colors = { actif: 'green', expire: 'red', resilie: 'default', renouvele: 'blue' };
                return <Tag color={colors[s] || 'default'}>{s}</Tag>;
            },
        },
        {
            title: 'Prime TTC',
            dataIndex: ['contrat', 'primeTTC'],
            key: 'prime',
            render: (v) => `${(v || 0).toLocaleString()} FCFA`,
            align: 'right',
        },
        {
            title: 'Verse',
            dataIndex: 'totalVerse',
            key: 'verse',
            render: (v) => <span style={{ color: '#2e7d32' }}>{(v || 0).toLocaleString()} FCFA</span>,
            align: 'right',
        },
        {
            title: 'Reste',
            dataIndex: 'resteAPayer',
            key: 'reste',
            render: (v) => (
                <span style={{ color: v > 0 ? '#C8102E' : '#2e7d32', fontWeight: 600 }}>
                    {(v || 0).toLocaleString()} FCFA
                </span>
            ),
            align: 'right',
        },
    ];

    return (
        <div>
            <Title level={3}>
                <DollarOutlined style={{ marginRight: 8 }} />
                Ma situation financiere
            </Title>

            {/* Stats globales */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8}>
                    <Card>
                        <Statistic
                            title="Total primes"
                            value={data.totalPrimes || 0}
                            suffix="FCFA"
                            prefix={<FileProtectOutlined style={{ color: '#1A2B5F' }} />}
                            valueStyle={{ fontSize: 20 }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card>
                        <Statistic
                            title="Total verse"
                            value={data.totalVerse || 0}
                            suffix="FCFA"
                            prefix={<CheckCircleOutlined style={{ color: '#2e7d32' }} />}
                            valueStyle={{ fontSize: 20, color: '#2e7d32' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card>
                        <Statistic
                            title="Solde restant"
                            value={data.soldeGlobal || 0}
                            suffix="FCFA"
                            prefix={<WarningOutlined style={{ color: data.soldeGlobal > 0 ? '#C8102E' : '#2e7d32' }} />}
                            valueStyle={{ fontSize: 20, color: data.soldeGlobal > 0 ? '#C8102E' : '#2e7d32' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Detail par contrat */}
            <Card title="Detail par contrat">
                <Table
                    dataSource={data.situationParContrat || []}
                    columns={columns}
                    rowKey={(record) => record.contrat?._id}
                    pagination={false}
                    scroll={{ x: 900 }}
                    locale={{ emptyText: 'Aucun contrat' }}
                    summary={() => (
                        <Table.Summary.Row style={{ fontWeight: 700, background: '#fafafa' }}>
                            <Table.Summary.Cell colSpan={5}>TOTAL</Table.Summary.Cell>
                            <Table.Summary.Cell align="right">{(data.totalPrimes || 0).toLocaleString()} FCFA</Table.Summary.Cell>
                            <Table.Summary.Cell align="right" style={{ color: '#2e7d32' }}>{(data.totalVerse || 0).toLocaleString()} FCFA</Table.Summary.Cell>
                            <Table.Summary.Cell align="right" style={{ color: '#C8102E' }}>{(data.soldeGlobal || 0).toLocaleString()} FCFA</Table.Summary.Cell>
                        </Table.Summary.Row>
                    )}
                />
            </Card>
        </div>
    );
}
